import * as React from "react";
import { useContext, useState } from "react";
import * as Immutable from "immutable";
import { parse } from "qs";
import { Formik } from "formik";
import { Trans, useTranslation } from "react-i18next";
import useLocation from "routing/useLocation";
import Wizard from "components/common/Wizard";
import { Spinner } from "components/common";
import OktaMatchingGroupsProvider from "authentication/components/oidc/config/components/OktaMatchingGroupsProvider";
import payloadFromFormValues from "authentication/components/oidc/config/helpers/payloadFromFormValues";
import { OidcBackendMetaContext } from "authentication/components/oidc/config/components/OidcBackendMetaProvider";
import { OKTA_TYPE_KEY } from "authentication/components/oidc/constants";
import convertToSeconds from "authentication/helpers/convertToSeconds";
import useRoles from "common/hooks/useRoles";
import OidcGroupSyncForm from "authentication/components/oidc/config/components/OidcGroupSyncForm";
import formatAuthBackendValidationErrors from "authentication/helpers/formatAuthBackendValidationErrors";
import AuthBackendSubmitError from "authentication/components/AuthBackendSubmitError";
import ServerConfigurationForm from "./components/ServerConfigurationForm";
import GroupSyncForm from "./components/GroupSyncForm";
const defaultValues = {
  id: void 0,
  title: "",
  description: "",
  defaultRoles: Immutable.List(),
  defaultUserTimezone: void 0,
  config: {
    type: OKTA_TYPE_KEY,
    baseUrl: "",
    callbackUrl: "",
    clientId: "",
    clientSecret: { is_set: false },
    claims: {},
    tokenVerifierConnectTimeout: ""
  }
};
const BackendWizard = ({
  authenticationBackend = defaultValues,
  onSubmitForm,
  groupSyncValues = {},
  backendType,
  isOkta
}) => {
  var _a, _b;
  const { t } = useTranslation("enterprise_authentication");
  const location = useLocation();
  const searchParams = parse(location.search.slice(1));
  const [wizardStep, setWizardStep] = useState((_a = searchParams.initialStepKey) != null ? _a : "server_config");
  const [submitErrors, setSubmitErrors] = useState(void 0);
  const { backendGroupSyncIsActive, licenseIsValid, backendId } = useContext(OidcBackendMetaContext);
  const { title, description, config, defaultRoles, defaultUserTimezone } = authenticationBackend;
  const { callbackUrl, clientId, clientSecret, tokenVerifierConnectTimeout } = config;
  const baseUrl = isOkta ? config.oktaBaseUrl : config.baseUrl;
  const claims = isOkta ? void 0 : config.claims;
  const { isLoading, roles } = useRoles();
  if (isLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const defaultCreateRoleId = (_b = roles.find((role) => role.name === "Reader")) == null ? void 0 : _b.id;
  const defaultRolesValues = defaultRoles.toArray().join(",");
  const hasSecret = clientSecret == null ? void 0 : clientSecret.is_set;
  let hasApiToken;
  let initialValues = {
    title,
    description,
    ...isOkta ? { oktaBaseUrl: baseUrl } : { baseUrl, claims },
    ...!isOkta && { groupsClaim: void 0 },
    callbackUrl,
    clientId,
    type: backendType,
    clientSecret: hasSecret ? void 0 : "",
    tokenVerifierConnectTimeout: convertToSeconds(tokenVerifierConnectTimeout) || 10,
    defaultRoles: defaultRolesValues === "" ? defaultCreateRoleId : defaultRolesValues,
    defaultUserTimezone,
    teamDefaultRoles: void 0,
    teamSelectionType: void 0,
    teamSelection: Immutable.Set(),
    synchronizeGroups: void 0,
    oktaApiToken: void 0
  };
  if (groupSyncValues) {
    const { oktaApiToken, ...rest } = groupSyncValues;
    hasApiToken = oktaApiToken == null ? void 0 : oktaApiToken.is_set;
    initialValues = { ...initialValues, oktaApiToken: hasApiToken ? void 0 : "", ...rest };
  }
  const _goToStep = (step) => setWizardStep(step);
  const _handleSubmit = (values, { setErrors, setSubmitting }) => {
    const _submit = () => {
      onSubmitForm(values, backendGroupSyncIsActive, licenseIsValid).catch((error) => {
        var _a2, _b2;
        if (typeof ((_b2 = (_a2 = error == null ? void 0 : error.additional) == null ? void 0 : _a2.body) == null ? void 0 : _b2.errors) === "object") {
          const backendValidationErrors = formatAuthBackendValidationErrors(error.additional.body.errors);
          setErrors(backendValidationErrors);
        } else {
          setSubmitErrors(error);
        }
      }).finally(() => {
        setSubmitting(false);
      });
    };
    if (backendGroupSyncIsActive && !values.synchronizeGroups) {
      if (
        // eslint-disable-next-line no-alert
        window.confirm(t("oidc.backend_wizard.confirm_remove_group_sync"))
      ) {
        _submit();
      }
      setSubmitting(false);
      return;
    }
    _submit();
  };
  return /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues,
      enableReinitialize: true,
      onSubmit: _handleSubmit,
      validateOnBlur: false,
      validateOnChange: false,
      validateOnMount: false
    },
    ({ isSubmitting, values, setFieldValue, handleSubmit, validateForm }) => {
      const steps = [
        {
          key: "server_config",
          title: /* @__PURE__ */ React.createElement("span", null, t("oidc.backend_wizard.server_configuration")),
          component: /* @__PURE__ */ React.createElement(
            ServerConfigurationForm,
            {
              values,
              roles,
              authenticationBackend,
              isSubmitting,
              hasSecret,
              isOkta,
              setFieldValue,
              validateForm,
              submitAllError: submitErrors && /* @__PURE__ */ React.createElement(AuthBackendSubmitError, { error: submitErrors, backendId }),
              goToNext: () => _goToStep("group_sync")
            }
          )
        },
        {
          key: "group_sync",
          title: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "oidc.backend_wizard.group_sync_title", components: { small: /* @__PURE__ */ React.createElement("small", null) } })),
          component: isOkta ? /* @__PURE__ */ React.createElement(
            GroupSyncForm,
            {
              values,
              setFieldValue,
              isSubmitting,
              hasApiToken,
              handleSubmit,
              validateForm,
              submitAllError: submitErrors && /* @__PURE__ */ React.createElement(AuthBackendSubmitError, { error: submitErrors, backendId }),
              goToPrevious: () => _goToStep("server_config")
            }
          ) : /* @__PURE__ */ React.createElement(
            OidcGroupSyncForm,
            {
              values,
              isSubmitting,
              handleSubmit,
              validateForm,
              submitAllError: submitErrors && /* @__PURE__ */ React.createElement(AuthBackendSubmitError, { error: submitErrors, backendId }),
              goToPrevious: () => _goToStep("server_config")
            }
          )
        }
      ];
      return /* @__PURE__ */ React.createElement(OktaMatchingGroupsProvider, { prepareSubmitPayload: payloadFromFormValues }, /* @__PURE__ */ React.createElement(
        Wizard,
        {
          hidePreviousNextButtons: true,
          justified: true,
          horizontal: true,
          containerClassName: "",
          onStepChange: setWizardStep,
          activeStep: wizardStep,
          steps
        }
      ));
    }
  );
};
export default BackendWizard;
